import React from 'react';
import { Typography, Box, Stack } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { authApi } from '../../api/auth';
import PageLayout from '../../components/layout/PageLayout';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import BasicButton from '../../components/button/BasicButton';
import theme from '../../theme';
import CustomTextField from '../../components/common/CustomTextField';

const schema = yup.object().shape({
  currentPassword: yup.string().required('현재 비밀번호를 입력해 주세요.'),
  password: yup
    .string()
    .required('새 비밀번호를 입력해 주세요.')
    .min(8, '비밀번호는 문자, 숫자, 특수문자 포함 (8~20자)로 입력해야 합니다.')
    .max(20, '비밀번호는 문자, 숫자, 특수문자 포함 (8~20자)로 입력해야 합니다.')
    .matches(
      /^(?=.*[a-zA-Z])(?=.*[0-9])(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?])/,
      '비밀번호는 문자, 숫자, 특수문자 포함 (8~20자)로 입력해야 합니다.',
    ),
  confirmPassword: yup
    .string()
    .required('새 비밀번호 확인을 입력해 주세요.')
    .oneOf([yup.ref('password')], '비밀번호가 일치하지 않습니다.'),
});

const PasswordChange = () => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(schema),
  });

  const navigate = useNavigate();

  const onSubmit = async (formData) => {
    try {
      await authApi.changePassword(formData.currentPassword, formData.password);
      // 성공 시 바로 마이페이지로 이동
      navigate('/mypage');
    } catch (err) {
      console.error(err);
    } finally {
      reset();
    }
  };

  return (
    <PageLayout>
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Stack sx={{ width: { lg: '33%', md: '49%', xs: '100%' } }} spacing={'40px'}>
            <Stack spacing={'16px'}>
              <Typography
                fontSize={{ lg: 24, md: 22, xs: 20 }}
                fontWeight="bold"
                textAlign="center"
              >
                비밀번호 변경
              </Typography>
              <Typography
                fontSize="14px"
                textAlign="center"
                color={theme.palette.black[4]}
                align="center"
              >
                새로운 비밀번호를 설정해 주세요.
              </Typography>
            </Stack>

            <Stack spacing={2.5}>
              <Stack spacing={1}>
                <Typography variant="body3">
                  현재 비밀번호<span style={{ color: theme.palette.secondary.main }}>*</span>
                </Typography>
                <CustomTextField
                  type="password"
                  fullWidth
                  {...register('currentPassword')}
                  error={!!errors.currentPassword}
                  helperText={errors.currentPassword?.message}
                  placeholder="현재 비밀번호를 입력해 주세요."
                />
              </Stack>
              <Stack spacing={1}>
                <Typography variant="body3">
                  새 비밀번호<span style={{ color: theme.palette.secondary.main }}>*</span>
                </Typography>
                <CustomTextField
                  type="password"
                  fullWidth
                  {...register('password')}
                  error={!!errors.password}
                  helperText={errors.password?.message}
                  placeholder="문자, 숫자, 특수문자 포함 8~20자"
                />
              </Stack>
              <Stack spacing={1}>
                <Typography variant="body3">
                  새 비밀번호 확인<span style={{ color: theme.palette.secondary.main }}>*</span>
                </Typography>
                <CustomTextField
                  type="password"
                  fullWidth
                  {...register('confirmPassword')}
                  error={!!errors.confirmPassword}
                  helperText={errors.confirmPassword?.message}
                  placeholder="문자, 숫자, 특수문자 포함 8~20자"
                />
              </Stack>
            </Stack>

            <Box mt={'30px'} sx={{ width: '100%' }}>
              <BasicButton
                type="submit"
                fullWidth
                disabled={isSubmitting}
                onClick={handleSubmit(onSubmit)}
              >
                비밀번호 변경
              </BasicButton>
            </Box>
          </Stack>
        </Box>
      </Box>
    </PageLayout>
  );
};

export default PasswordChange;
